import type { Session } from "@supabase/supabase-js";
import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid
} from "recharts";
import { adminRequest, type DashboardSummary, type DashboardTrends } from "../adminApi";
import { MetricCard } from "../components/MetricCard";
import { Status } from "../components/Status";
import { LoadingPanel } from "../components/LoadingPanel";

const PIE_COLORS = ["#E31937", "#1F8A5B", "#83838C", "#3E7BE6", "#C98A2B"];

const TOOLTIP_STYLE = {
  background: "#1C1C1E",
  border: "1px solid #38383A",
  borderRadius: 6,
  fontSize: "0.75rem",
  outline: "none"
};

export function DashboardPage({
  mfaToken,
  session,
  setNotice
}: {
  mfaToken: string | null;
  session: Session;
  setNotice(n: { tone: "error" | "success"; text: string } | null): void;
}) {
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [trends, setTrends] = useState<DashboardTrends | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([
        adminRequest<DashboardSummary>("/dashboard", { mfaToken, session }),
        adminRequest<DashboardTrends>("/dashboard/trends", { mfaToken, session })
      ]);
      setDashboard(d);
      setTrends(t);
    } catch (caught) {
      setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Could not load dashboard." });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); }, []);

  const cards = useMemo(() => [
    { label: "Total users", value: dashboard?.total_users ?? "—" },
    { label: "Active users 7d", value: dashboard?.active_users_7d ?? "—" },
    { label: "Pending invites", value: dashboard?.pending_invitations ?? "—" },
    { label: "Linked vehicles", value: dashboard?.linked_vehicles ?? "—" },
    { label: "Workflows", value: dashboard?.total_workflows ?? "—" },
    { label: "Accepted invites", value: dashboard?.accepted_invitations ?? "—" }
  ], [dashboard]);

  if (loading) return <LoadingPanel label="Loading dashboard…" />;

  return (
    <div className="stack">
      <section className="section">
        <div className="sectionHead">
          <div>
            <h1>Dashboard</h1>
            <p>Operational view across users, invites, vehicles, workflows, and commands.</p>
          </div>
          <button onClick={() => void refresh()} type="button">Refresh</button>
        </div>
        <div className="metrics">
          {cards.map((card) => <MetricCard key={card.label} label={card.label} value={card.value} />)}
        </div>
      </section>

      {trends && (
        <section className="section">
          <div className="chartGrid">
            <div className="panel chartPanel">
              <h2>Runs per day</h2>
              <div className="chartContainer">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={trends.runs_per_day}>
                    <XAxis
                      dataKey="date"
                      tick={{ fill: "#83838C", fontSize: 10 }}
                      tickFormatter={(d: string) => d.slice(5)}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis allowDecimals={false} tick={{ fill: "#83838C", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Bar dataKey="count" fill="#E31937" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="panel chartPanel">
              <h2>Command outcomes</h2>
              <div className="chartContainer">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={trends.command_outcomes}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      innerRadius={40}
                    >
                      {trends.command_outcomes.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="pieLegend">
                  {trends.command_outcomes.map((entry, i) => (
                    <span key={entry.status} className="pieLegendItem">
                      <span className="pieDot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {entry.status}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="panel chartPanel" style={{ marginTop: 16 }}>
            <h2>User growth (30 days)</h2>
            <div className="chartContainer">
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trends.user_growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#38383A" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#83838C", fontSize: 10 }}
                    tickFormatter={(d: string) => d.slice(5)}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis allowDecimals={false} tick={{ fill: "#83838C", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Line type="monotone" dataKey="cumulative" stroke="#3E7BE6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="activityGrid">
          <RecentRuns runs={dashboard?.recent_runs ?? []} />
          <RecentCommands commands={dashboard?.recent_commands ?? []} />
        </div>
      </section>
    </div>
  );
}

function RecentRuns({ runs }: { runs: DashboardSummary["recent_runs"] }) {
  return (
    <div className="panel">
      <h2>Recent runs</h2>
      <div className="list">
        {runs.map((run) => (
          <div className="listItem" key={run.id}>
            <div>
              <strong>{run.workflow_name ?? "Workflow"}</strong>
              <span>{run.user_email ?? "Unknown user"} · {run.vehicle_name ?? "Vehicle"}</span>
            </div>
            <Status value={run.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

function RecentCommands({ commands }: { commands: DashboardSummary["recent_commands"] }) {
  return (
    <div className="panel">
      <h2>Recent commands</h2>
      <div className="list">
        {commands.map((command, index) => (
          <div className="listItem" key={`${command.run_id}-${index}`}>
            <div>
              <strong>{command.command ?? "Delay"}</strong>
              <span>{command.user_email ?? "Unknown user"} · {command.workflow_name ?? "Workflow"}</span>
            </div>
            <Status value={command.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
